import React from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, resolveApiAssetUrl } from '../lib/api'
import Loading from '../ui/Loading'
import InlineError from '../ui/InlineError'
import { createCategoryLandingDefaultFormFields, normalizeCategoryLandingLayoutConfig } from '../templates/categoryLanding/registry'
import { renderCategoryLandingLayout } from '../templates/categoryLanding/layouts/registry'
import { AlertTriangle } from 'lucide-react'

function parseJsonArray(raw, fallback = []) {
  if (!raw) return fallback
  if (Array.isArray(raw)) return raw
  try {
    const parsed = JSON.parse(String(raw))
    return Array.isArray(parsed) ? parsed : fallback
  } catch {
    return fallback
  }
}

function parseJsonObject(raw, fallback = {}) {
  if (!raw) return fallback
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw
  try {
    const parsed = JSON.parse(String(raw))
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : fallback
  } catch {
    return fallback
  }
}

export default function CategoryLandingPagePreview() {
  const { id } = useParams()
  const detailQuery = useQuery({
    queryKey: ['category-landing-preview', id],
    queryFn: async () => {
      const res = await api.get(`/category-landing-pages/me/${id}`, { __silent: true })
      return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
    },
    enabled: !!id,
  })

  if (detailQuery.isLoading) return <Loading />
  if (detailQuery.isError) return <InlineError message={detailQuery.error?.message || '미리보기 로드 실패'} />
  const data = detailQuery.data
  if (!data) return <InlineError message="페이지가 없습니다." />

  const contentSections = parseJsonArray(data?.contentSections, [])
  const customConfig = normalizeCategoryLandingLayoutConfig(parseJsonObject(data?.customConfig, {}))
  const introStats = Array.isArray(customConfig?.introStats) ? customConfig.introStats : []
  const formFields = parseJsonArray(data?.formFields, createCategoryLandingDefaultFormFields({ includeMessage: true }))
  const formBorderColor = customConfig?.formBorderColor || '#cbd5e1'
  const formBorderWidth = customConfig?.formBorderWidth || 1
  const recommendedTitle = customConfig?.recommendedTitle || '추천상품'
  const recommendedProducts = Array.isArray(customConfig?.recommendedProducts) ? customConfig.recommendedProducts : []
  const layoutKey = customConfig?.templateStyle || 'default'
  const adReviewFooterText = String(data?.adReviewNotice || '').trim()

  const previewFormCard = (
    <div className="w-[380px] max-w-full rounded-[28px] bg-white p-7 shadow-lg" style={{ border: `${formBorderWidth}px solid ${formBorderColor}` }}>
      <h2 className="mb-5 text-center text-[28px] font-bold leading-tight text-slate-900">{data.formTitle || '상담 신청하기'}</h2>
      <div className="mb-5 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-base text-amber-800">
        <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span>미리보기 모드: 실제 제출은 되지 않습니다.</span>
      </div>
      <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); alert('미리보기 모드에서는 제출할 수 없습니다.'); }}>
        {formFields.map((f, idx) => {
          const name = String(f?.name || `field_${idx}`)
          const type = String(f?.type || 'text')
          const label = f?.label || name
          const required = Boolean(f?.required)
          if (type === 'textarea') {
            return (
              <div key={name}>
                <label className="block text-base font-medium text-slate-600">{label}{required ? ' *' : ''}</label>
                <textarea className="mt-2 min-h-[110px] w-full rounded-xl border px-4 py-3 text-base bg-slate-50" disabled placeholder="미리보기 모드" />
              </div>
            )
          }
          return (
            <div key={name}>
              <label className="block text-base font-medium text-slate-600">{label}{required ? ' *' : ''}</label>
              <input type={type === 'tel' ? 'tel' : 'text'} className="mt-2 w-full rounded-xl border px-4 py-3 text-base bg-slate-50" disabled placeholder="미리보기 모드" />
            </div>
          )
        })}
        <div className="flex items-start gap-3 text-sm leading-6 text-slate-600">
          <input type="checkbox" disabled className="mt-0.5" />
          <span>개인정보 수집 및 이용동의 (미리보기)</span>
        </div>
        <div className="flex items-start gap-3 text-sm leading-6 text-slate-600">
          <input type="checkbox" disabled className="mt-0.5" />
          <span>개인정보 제3자 제공 동의 (미리보기)</span>
        </div>
        <button type="submit" className="w-full cursor-not-allowed rounded-2xl bg-slate-400 py-4 text-base font-semibold text-white" disabled>
          상담 신청 (미리보기)
        </button>
      </form>
    </div>
  )

  const previewRecommendedProducts = (
    <div className="w-[132px]">
      <div className="mb-3 text-center text-sm font-bold text-white">
        <span className="inline-flex rounded-full px-3 py-1.5" style={{ backgroundColor: formBorderColor }}>{recommendedTitle}</span>
      </div>
      <div className="rounded-[24px] border border-slate-200 bg-white p-3 shadow-sm">
        {recommendedProducts.length > 0 ? (
          <div className="space-y-2">
            {recommendedProducts.map((item, idx) => (
              <div key={idx} className="flex flex-col items-center gap-1 rounded-2xl px-2 py-4 text-center">
                <div className="text-[15px] font-semibold leading-5 text-slate-900 break-keep">{item?.title || `추천상품 ${idx + 1}`}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="px-2 py-4 text-center text-sm leading-5 text-slate-500 break-keep">
            공개된 다른 카테고리 랜딩이 있으면 자동으로 노출됩니다.
          </div>
        )}
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-white text-slate-900 relative">
      {renderCategoryLandingLayout(layoutKey, {
        mode: 'preview',
        heroTitle: data.heroTitle || '제목 없음',
        heroSubtitle: data.heroSubtitle || '',
        heroImageFileId: data.heroImageFileId || '',
        sections: contentSections,
        formEnabled: data.formEnabled !== false,
        formTitle: data.formTitle || '상담 신청하기',
        introStats: data.category === 'PERSONAL_INTRO' ? introStats : [],
        customConfig,
        resolveAssetUrl: resolveApiAssetUrl,
        formCard: previewFormCard,
        recommendedProductsCard: previewRecommendedProducts,
        previewBanner: <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-sm text-amber-800">미리보기 모드 (로그인 사용자 전용)</div>,
      })}
      {adReviewFooterText ? (
        <footer className="border-t-2 border-slate-200 bg-slate-50 px-4 py-8 text-slate-700 md:px-6">
          <div className="mx-auto max-w-3xl">
            <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">광고 심의필·준법 표기</div>
            <div className="mt-2 whitespace-pre-line text-xs leading-relaxed text-slate-600 md:text-sm">
              {adReviewFooterText}
            </div>
          </div>
        </footer>
      ) : (
        <footer className="border-t border-amber-200 bg-amber-50 px-4 py-4 text-center text-xs text-amber-900">
          하단 광고 심의필 문구를 입력하고 저장하면 이 영역에 미리보기됩니다.
        </footer>
      )}
    </div>
  )
}
